import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { LatLngLiteral } from "leaflet";
import { compact } from "lodash";
import { useInternal } from "uikit";

import { Map } from "../../../../../services";
import { useTypedDispatch, useTypedSelector } from "../../../../../redux/store";
import orderPage from "../../../../../redux/reducers/OrdersPage";

/** Регістр і пробіли; "76В" дорівнює "76в". Інакше searchHouse звужував усі 76× до "76". */
function normalizeHouseForMatch(value: unknown): string {
	return String(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/\s+/g, "");
}

const useGetCoordinatesByHouse: useGetCoordinatesByHouse.Props2 = () => {
	const dispatch = useTypedDispatch();
	const language = useTypedSelector((state) => state.session.language);
	const { searchRadiusMeters: searchRadius } = useTypedSelector(
		(state) => state.settings.map,
	);

	const [coordinates, setCoordinates] = useInternal<LatLngLiteral | null>(
		null,
	);
	const [response, setResponse] = useInternal<Map.Search.Object | null>(null);
	const [house, setHouse] = useInternal<string | null>(null);

	const searchQuery = useCallback<
		useGetCoordinatesByHouse.Props["searchQuery"]
	>((data) => {
		const settlement = data.settlement ? `${data.settlement.trim()}, ` : "";
		const settlementType = data.settlementType
			? `${data.settlementType.trim()} `
			: "";
		const street = data.street ? `${data.street.trim()}, ` : "";
		const streetType = data.streetType ? `${data.streetType.trim()} ` : "";
		const house = data.house ? ` ${data.house.trim()}` : "";
		const district = data.district ? `${data.district.trim()}, ` : "";

		return `${settlementType}${settlement}${streetType}${street}${district}${house}`;
	}, []);

	// Пошук "наступного" будинку тієї ж парності серед уже відомих номерів.
	// Потрібен для Visicom (його /houses часто неповний — введений номер може
	// не існувати, але є сусідній парний/непарний з більшим номером).
	const searchHouse = useCallback<
		useGetCoordinatesByHouse.Props["searchHouse"]
	>((house: string, houses: Map.Search.Object["houses"]) => {
		const want = normalizeHouseForMatch(house);
		const list = houses ?? [];
		if (want && list.length) {
			const exact = list.find(
				(h) => normalizeHouseForMatch(h?.number ?? "") === want,
			);
			if (exact?.number) return exact.number;
		}

		// eslint-disable-next-line no-bitwise
		const evenOrOdd = (num: number) => (Number(num) & 1) === 0;
		const homeMatch = house.match(/\d+/);
		const home = homeMatch?.[0] ? Number(homeMatch[0]) : NaN;

		if (!Number.isFinite(home)) {
			const loose = list.find((h) =>
				String(h?.number ?? "").includes(house),
			);
			return loose?.number;
		}

		const numbered = compact(
			list.map((h) => {
				const raw = String(h?.number ?? "").trim();
				if (!raw) return undefined;
				const n = Number(raw.match(/\d+/)?.[0] ?? NaN);
				if (!Number.isFinite(n)) return undefined;
				return { raw, n };
			}),
		);

		const picked = numbered.find((c) => {
			if (evenOrOdd(c.n) !== evenOrOdd(home)) return false;
			return c.n >= home;
		});

		return picked?.raw;
	}, []);

	const get = useCallback<useGetCoordinatesByHouse.Props["get"]>(
		async (data, houseQuery) => {
			const optionsType: Map.Search.Type[] = [
				"favorite",
				"localObject",
				"object",
			];

			if (data && houseQuery) {
				setHouse(houseQuery);
				if (data.type && optionsType.includes(data.type)) {
					setResponse(data as Map.Search.Object);

					return;
				}

				const hqNorm = normalizeHouseForMatch(houseQuery);
				if (data.type === "address" && hqNorm) {
					const coords = data.coordinates;
					const hasCoords =
						coords &&
						typeof coords.lat === "number" &&
						typeof coords.lng === "number" &&
						!(coords.lat === 0 && coords.lng === 0);
					const dataNumNorm = normalizeHouseForMatch(
						data.number || data.house || "",
					);
					if (hasCoords && dataNumNorm && dataNumNorm === hqNorm) {
						setResponse(data as Map.Search.Object);
						setCoordinates(coords);
						return;
					}
				}

				const exist = data.houses?.find(
					(h) =>
						normalizeHouseForMatch(h.number) ===
						normalizeHouseForMatch(houseQuery),
				);

				// OSM (Nominatim) повертає повні street/houses одразу, тому
				// фронт-end логіка з підбором "next house" та повторним пошуком
				// не потрібна. Для Visicom — навпаки, без цього часто немає координат.
				const isOsm = data?.searchType === "osm_nominatim";

				const searchOptions: Map.Search.Options = {
					language,
					searchType: "address",
					country: "ua",
					// country: "md",
					near: {
						point: data.coordinates,
						radius: searchRadius,
					},
					placeId: data.placeId,
				};

				setCoordinates(data.coordinates);

				if (!exist) {
					if (isOsm) {
						const query = searchQuery({
							...data,
							house: houseQuery,
						});
						const result = await Map.search(query, searchOptions);

						if (result.objects?.length) {
							const mainArr: Map.Search.Object[] = [];
							const other: Map.Search.Object[] = [];
							result.objects.forEach((item) => {
								if (
									(item?.settlement?.toLowerCase() ===
										data?.settlement?.toLowerCase() &&
										item?.street?.toLowerCase() ===
											data?.street?.toLowerCase()) ||
									item?.searchType?.toLowerCase() ===
										"google_street_address"
								) {
									mainArr.push(item);
								} else if (
									item?.settlement?.toLowerCase() ===
									data?.settlement?.toLowerCase()
								) {
									other.push(item);
								}
							});

							const qNorm = houseQuery?.toLowerCase();

							let houseData = mainArr.find(
								(item) => item.number?.toLowerCase() === qNorm,
							);

							// OSM/Nominatim: nearest building може мати інший housenumber.
							if (!houseData && mainArr.length) {
								houseData =
									mainArr.find(
										(item) => item.type === "address",
									) || mainArr[0];
							}

							if (!houseData) {
								houseData = mainArr.find((item) =>
									item.houses?.some(
										(h) =>
											h.number?.toLowerCase() === qNorm,
									),
								);
							}

							if (!houseData) {
								houseData = other.find(
									(item) =>
										item.number?.toLowerCase() === qNorm,
								);
							}
							if (!houseData && other.length) {
								[houseData] = other;
							}

							const [firstMain] = mainArr;
							const [firstOther] = other;
							const item = firstMain || firstOther;

							const defineResponse = houseData || item;
							if (item?.searchType === "google_street_address") {
								defineResponse.street = data.street;
								defineResponse.number =
									defineResponse?.houses?.[0]?.number || "";
								defineResponse.house =
									defineResponse?.houses?.[0]?.number || "";
							}

							setResponse(defineResponse);
							setCoordinates(
								houseData?.coordinates || item.coordinates,
							);
						} else {
							setResponse({
								...data,
								house: houseQuery,
							} as Map.Search.Object);
						}
					} else {
						const nextHouse = searchHouse(houseQuery, data.houses);
						const query = searchQuery({
							...data,
							house: nextHouse || houseQuery,
						});
						const result = await Map.search(query, searchOptions);

						if (result.objects?.length) {
							const mainArr: Map.Search.Object[] = [];
							const other: Map.Search.Object[] = [];
							const mainListNumber: Required<
								Map.Search.Object["houses"]
							> = [];
							let nextHome: string = nextHouse || houseQuery;
							result.objects.forEach((item, id) => {
								if (
									(item?.settlement?.toLowerCase() ===
										data?.settlement?.toLowerCase() &&
										item?.street?.toLowerCase() ===
											data?.street?.toLowerCase()) ||
									item?.searchType?.toLowerCase() ===
										"google_street_address"
								) {
									mainArr.push(item);
									if (item?.number) {
										mainListNumber.push({
											number: item.number,
											id: `${id}`,
										});
									}
								} else if (
									item?.settlement?.toLowerCase() ===
									data?.settlement?.toLowerCase()
								) {
									other.push(item);
								}
							});

							let houseData = mainArr.find(
								(item) =>
									normalizeHouseForMatch(
										item.number ?? "",
									) ===
									normalizeHouseForMatch(nextHouse ?? ""),
							);

							if (mainArr.length && !houseData) {
								const houses = mainArr?.find(
									(item) => item.houses?.length,
								)?.houses;
								if (houses && Array.isArray(houses)) {
									const nextHouse2 = searchHouse(
										houseQuery,
										houses.filter(
											(h) =>
												normalizeHouseForMatch(
													h.number,
												) !==
												normalizeHouseForMatch(
													houseQuery,
												),
										),
									);

									if (nextHouse2) nextHome = nextHouse2;
								}
							}

							if (
								normalizeHouseForMatch(nextHome) !==
								normalizeHouseForMatch(houseQuery)
							) {
								const query2 = searchQuery({
									...data,
									house: nextHome,
								});

								const result2 = await Map.search(
									query2,
									searchOptions,
								);

								const nexRes = result2.objects.find(
									(item) =>
										item?.settlement?.toLowerCase() ===
											data?.settlement?.toLowerCase() &&
										item?.street?.toLowerCase() ===
											data?.street?.toLowerCase() &&
										normalizeHouseForMatch(item.number) ===
											normalizeHouseForMatch(nextHome),
								);
								if (nexRes) houseData = nexRes;
							}

							if (!houseData && mainListNumber.length) {
								const nextHouse2 = searchHouse(
									houseQuery,
									mainListNumber,
								);

								if (nextHouse2) {
									houseData = mainArr.find(
										(item) =>
											normalizeHouseForMatch(
												item.number ?? "",
											) ===
											normalizeHouseForMatch(nextHouse2),
									);
								}
							}

							if (!houseData) {
								houseData = other.find(
									(item) =>
										normalizeHouseForMatch(
											item.number ?? "",
										) === normalizeHouseForMatch(nextHouse),
								);
							}

							const item = mainArr[0] || other[0];

							const defineResponse = houseData || item;
							if (item?.searchType === "google_street_address") {
								defineResponse.street = data.street;
								defineResponse.number =
									defineResponse?.houses?.[0]?.number || "";
								defineResponse.house =
									defineResponse?.houses?.[0]?.number || "";
							}

							setResponse(defineResponse);
							setCoordinates(
								houseData?.coordinates || item?.coordinates,
							);
						} else {
							setResponse({
								...data,
								house: houseQuery,
							} as Map.Search.Object);
						}
					}
				} else if (isOsm) {
					const query = searchQuery({
						...data,
						house: houseQuery,
					});
					const result = await Map.search(query, searchOptions);

					if (result.objects.length) {
						const mainArr: Map.Search.Object[] = [];
						const other: Map.Search.Object[] = [];

						result.objects.forEach((item) => {
							if (
								(item?.settlement?.toLowerCase() ===
									data?.settlement?.toLowerCase() &&
									item?.street?.toLowerCase() ===
										data?.street?.toLowerCase()) ||
								item?.searchType?.toLowerCase() ===
									"google_street_address"
							) {
								mainArr.push(item);
							} else if (
								item?.settlement?.toLowerCase() ===
								data?.settlement?.toLowerCase()
							) {
								other.push(item);
							}
						});

						const qNorm = houseQuery?.toLowerCase();

						let houseData = mainArr.find(
							(item) => item.number?.toLowerCase() === qNorm,
						);

						if (!houseData && mainArr.length) {
							houseData =
								mainArr.find(
									(item) => item.type === "address",
								) || mainArr[0];
						}

						if (!houseData) {
							houseData = mainArr.find((item) =>
								item.houses?.some(
									(h) => h.number?.toLowerCase() === qNorm,
								),
							);
						}

						if (!houseData) {
							houseData = other.find(
								(item) => item.number?.toLowerCase() === qNorm,
							);
						}
						if (!houseData && other.length) {
							[houseData] = other;
						}

						if (houseData) {
							setResponse(houseData);
							setCoordinates(houseData?.coordinates ?? null);
						} else {
							setResponse({
								...data,
								house: houseQuery,
								number: houseQuery,
							} as Map.Search.Object);
						}
					} else {
						setResponse({
							...data,
							house: houseQuery,
							number: houseQuery,
						} as Map.Search.Object);
					}
				} else {
					const query = searchQuery({
						...data,
						house: houseQuery,
					});
					const result = await Map.search(query, searchOptions);

					if (result.objects.length) {
						const mainArr: Map.Search.Object[] = [];
						const mainListNumber: Required<
							Map.Search.Object["houses"]
						> = [];
						const other: Map.Search.Object[] = [];
						let nextHome: string = houseQuery;

						result.objects.forEach((item, id) => {
							if (
								(item?.settlement?.toLowerCase() ===
									data?.settlement?.toLowerCase() &&
									item?.street?.toLowerCase() ===
										data?.street?.toLowerCase()) ||
								item?.searchType?.toLowerCase() ===
									"google_street_address"
							) {
								mainArr.push(item);
								if (item?.number) {
									mainListNumber.push({
										number: item.number,
										id: `${id}`,
									});
								}
							} else if (
								item?.settlement?.toLowerCase() ===
								data?.settlement?.toLowerCase()
							) {
								other.push(item);
							}
						});

						let houseData = mainArr.find(
							(item) =>
								normalizeHouseForMatch(item.number ?? "") ===
								normalizeHouseForMatch(houseQuery),
						);

						if (mainArr.length && !houseData) {
							const houses = mainArr?.find(
								(item) => item.houses?.length,
							)?.houses;
							if (houses && Array.isArray(houses)) {
								const nextHouseFromList = searchHouse(
									houseQuery,
									houses.filter(
										(h) =>
											normalizeHouseForMatch(h.number) !==
											normalizeHouseForMatch(houseQuery),
									),
								);

								if (nextHouseFromList)
									nextHome = nextHouseFromList;
							}
						}

						if (
							normalizeHouseForMatch(nextHome) !==
							normalizeHouseForMatch(houseQuery)
						) {
							const query2 = searchQuery({
								...data,
								house: nextHome,
							});

							const result2 = await Map.search(
								query2,
								searchOptions,
							);

							const nexRes = result2.objects.find(
								(item) =>
									item?.settlement?.toLowerCase() ===
										data?.settlement?.toLowerCase() &&
									item?.street?.toLowerCase() ===
										data?.street?.toLowerCase() &&
									normalizeHouseForMatch(item.number) ===
										normalizeHouseForMatch(nextHome),
							);
							if (nexRes) houseData = nexRes;
						}

						if (!houseData && mainListNumber.length) {
							const nextHouse2 = searchHouse(
								houseQuery,
								mainListNumber,
							);

							if (nextHouse2) {
								houseData = mainArr.find(
									(item) =>
										normalizeHouseForMatch(
											item.number ?? "",
										) ===
										normalizeHouseForMatch(nextHouse2),
								);
							}
						}

						if (!houseData) {
							houseData = other.find(
								(item) =>
									normalizeHouseForMatch(
										item.number ?? "",
									) === normalizeHouseForMatch(houseQuery),
							);
						}

						if (houseData) {
							setResponse(houseData);
							setCoordinates(houseData?.coordinates ?? null);
						}
					}
				}
			}

			if (data?.type && optionsType.includes(data.type) && !houseQuery) {
				("");
				setResponse(data as Map.Search.Object);
				setCoordinates(data.coordinates);
			}
		},

		[
			language,
			searchHouse,
			searchQuery,
			searchRadius,
			setCoordinates,
			setHouse,
			setResponse,
		],
	);

	useMemo(() => {
		if (coordinates) dispatch(orderPage.actions.setMapCenter(coordinates));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [coordinates]);

	const reset = useCallback(() => {
		setResponse(null);
		setHouse(null);
	}, [setResponse, setHouse]);

	return useMemo<useGetCoordinatesByHouse.Props>(
		() => ({
			house,
			response,
			coordinates,
			reset,
			get,
			setHouse,
			setResponse,
			searchHouse,
			searchQuery,
		}),
		[
			house,
			response,
			coordinates,
			reset,
			get,
			setHouse,
			setResponse,
			searchHouse,
			searchQuery,
		],
	);
};

declare namespace useGetCoordinatesByHouse {
	type Props2 = () => Props;
	interface SearchQuery {
		street?: string;
		streetType?: string;
		settlement?: string;
		settlementType?: string;
		house?: string;
		district?: string;
	}

	interface Props {
		response: Map.Search.Object | null;
		setResponse: Dispatch<SetStateAction<Map.Search.Object | null>>;
		coordinates: LatLngLiteral | null;
		get: (
			data: Partial<Map.Search.Object> &
				SearchQuery & {
					coordinates: LatLngLiteral;
					houses?: Map.Search.Object["houses"];
				},
			houseQuery: string,
		) => Promise<any>;
		reset: () => void;
		house: string | null;
		setHouse: Dispatch<SetStateAction<string | null>>;
		searchHouse: (
			house: string,
			houses: Map.Search.Object["houses"],
		) => string | undefined;

		searchQuery: (data: SearchQuery) => string;
	}
}

export default useGetCoordinatesByHouse;
